import React, { memo, Fragment, useMemo, useEffect } from 'react';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { SettingFieldResponse } from '@business/entities/settingFields';
import { FormInput, FormSelect } from '@core/components/form';
import { Row, Col } from 'reactstrap';
import { CommonService } from '../common_service';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';

export function defineCustomAddressForm(field: SettingFieldResponse, fillRequired = true) {
  return formGroup({
    address: formControl<string>({
      placeholder: 'Address',
      required: field.required && fillRequired,
    }),
    addressLineTwo: formControl<string | null>({
      placeholder: 'Address Line 2',
    }),
    city: formControl<string>({
      placeholder: 'City',
      required: field.required && fillRequired,
    }),
    state: formControl<string>({
      placeholder: 'State / Province / Region',
      required: field.required && fillRequired,
    }),
    postal: formControl<string>({
      placeholder: 'Postal Code',
    }),
    country: formControl<string>({
      placeholder: 'Country',
      required: field.required && fillRequired,
      initialValue: field.value || 'US',
    }),
  });
}

export type CustomAddressForm = ReturnType<typeof defineCustomAddressForm>;
export type CustomAddressFormData = FormDataType<typeof defineCustomAddressForm>;

export interface AddressCustomFieldProps {
  form: CustomAddressForm;
}

export const AddressCustomField = memo((props: AddressCustomFieldProps) => {
  const { form } = props;
  const formValue = useNonNilObservable(form.value$);

  const countries = CommonService.getCountriesItems();

  const statesItems = useMemo(() => {
    if (formValue.country) {
      return CommonService.getStatesItems(formValue.country);
    }
    return [];
  }, [formValue]);

  useEffect(() => {
    form.controls.state.setConfig({ required: !!statesItems.length });
  }, [form, formValue.country, statesItems.length]);

  return (
    <Fragment>
      <FormInput control={form.controls.address} />
      <FormInput control={form.controls.addressLineTwo} />
      <FormSelect control={form.controls.country} items={countries} />
      <FormInput control={form.controls.city} />
      <Row>
        <Col md={6}>
          {!!statesItems.length && (
            <FormSelect
              placeholder={formValue.country === 'US' ? 'State' : 'State / Province / Region'}
              control={form.controls.state}
              items={statesItems}
            />
          )}
          {!statesItems.length && <FormInput control={form.controls.state} />}
        </Col>
        <Col md={6}>
          <FormInput control={form.controls.postal} />
        </Col>
      </Row>
    </Fragment>
  );
});
